import { createFileRoute } from "@tanstack/react-router";
import { ContentPage } from "@/components/ContentPage";
import { FaqSection, faqJsonLd, type FaqItem } from "@/components/FaqSection";
import { IMAGES } from "@/lib/images";

export const FAQS: FaqItem[] = [
  {
    q: "Vad kostar en konsultation och ett platsbesök?",
    a: "Konsultation, platsbesök, uppmätning och offert kostar inget i Skåne och Göteborg. Du förbinder dig inte till något och betalar först om du väljer att beställa en installation.",
  },
  {
    q: "Hur snabbt kan ni vara på plats?",
    a: "Inom 5–10 arbetsdagar i normalfallet, ofta snabbare. Vid akuta säkerhetsärenden (efter inbrott eller hot) prioriterar vi och kan vara på plats inom 24–48 timmar.",
  },
  {
    q: "Vilka områden täcker ni?",
    a: "Hela Skåne (Malmö, Lund, Helsingborg, Kristianstad, Ystad, Trelleborg, Landskrona, Eslöv, Hässleholm, Ängelholm, Staffanstorp, Lomma, Kävlinge, Höör, Bjuv, Svedala) och Göteborg med kringliggande kommuner (Mölndal, Partille, Kungsbacka, Lerum, Härryda). Vi tar även projekt utanför dessa områden vid större entreprenader.",
  },
  {
    q: "Hjälper ni till med projektering tillsammans med arkitekt?",
    a: "Ja. Vi arbetar regelbundet med arkitekter, inredare, byggherrar och fastighetsförvaltare i större ny- och ombyggnadsprojekt. Vi levererar tekniska underlag, produktdatablad, EPD-deklarationer och CAD-vänliga monteringsanvisningar.",
  },
  {
    q: "Får jag prover på olika filmtyper innan beslut?",
    a: "Ja, vid platsbesöket tar vi med provbitar så att du kan se hur filmerna ser ut på ditt glas och i ditt ljus. I större projekt kan vi även montera en testbit på ett fönster innan du bestämmer dig.",
  },
  {
    q: "Vad ingår i ett platsbesök?",
    a: "Vi går igenom vad du vill ändra, fotograferar och mäter alla glasytor samt analyserar glastyp och U-/G-värde. Utifrån prestanda och budget föreslår vi film, och du får en skriftlig offert med material, garanti, installation och totalpris specificerade.",
  },
];

export const Route = createFileRoute("/radgivning")({
  head: () => ({
    meta: [
      { title: "Rådgivning, platsbesök & projektering — fönsterfilm | BBN Nordic" },
      { name: "description", content: "Kostnadsfri rådgivning, platsbesök och projektering i hela Skåne och Göteborg. Vi hjälper dig välja rätt 3M- eller Solar Screen-film för ditt glas och din verksamhet." },
      { property: "og:title", content: "Rådgivning och service" },
      { property: "og:description", content: "Gratis rådgivning, platsbesök och projektering i hela Skåne och Göteborg." },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://bbnnordic.se/radgivning" },
      { property: "og:image", content: IMAGES.installer },
    ],
    links: [{ rel: "canonical", href: "https://bbnnordic.se/radgivning" }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify(faqJsonLd(FAQS)),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Hem", item: "https://bbnnordic.se/" },
            { "@type": "ListItem", position: 2, name: "Rådgivning", item: "https://bbnnordic.se/radgivning" },
          ],
        }),
      },
    ],
  }),
  component: () => (
    <ContentPage
      eyebrow="SERVICE"
      title="Rådgivning, platsbesök och projektering"
      subtitle="Vi kommer ut utan kostnad, mäter och fotograferar glasen. Sedan föreslår vi en film som passar. Du bestämmer om du vill gå vidare."
      heroImage={IMAGES.installer}
      breadcrumbs={[{ label: "Rådgivning" }]}
      sections={[
        {
          eyebrow: "VÅR PROCESS",
          heading: "Från platsbesök till färdig installation.",
          paragraphs: [
            "Vi börjar med att fråga vad du behöver hjälp med. Är det för varmt, bländar solen eller behöver butiken bättre skydd? Utifrån det väljer vi produkt från 3M eller Solar Screen. Om film inte behövs säger vi det.",
            "Allt vårt arbete utförs av certifierade installatörer och är försäkrat. Du får alltid en namngiven projektledare och ett fast pris efter platsbesök.",
          ],
          bullets: [
            "Kostnadsfritt platsbesök i hela Skåne och Göteborg",
            "Skriftlig offert med fast pris",
            "Projektering tillsammans med arkitekt, inredare eller fastighetsägare",
            "Montering som planeras efter er verksamhet",
            "10 års produktgaranti + 5 års arbetsgaranti",
          ],
          image: IMAGES.installer,
        },
        {
          eyebrow: "VERKSAMHETSOMRÅDE",
          heading: "Vi servar hela Skåne och Göteborg.",
          paragraphs: [
            "Vi utgår från Staffanstorp och arbetar i hela Skåne samt Göteborg med kringliggande kommuner. Inom de områdena ingår resa och etablering i offerten utan extra påslag.",
          ],
          bullets: [
            "Skåne — Malmö, Lund, Helsingborg, Kristianstad, Ystad, Trelleborg, Landskrona",
            "Mellanskåne — Eslöv, Hässleholm, Höör, Bjuv, Svedala, Kävlinge, Staffanstorp",
            "Skånes västkust — Lomma, Bjärred, Ängelholm, Båstad",
            "Göteborg — centrum, Hisingen, Mölndal, Partille, Lerum, Härryda, Kungsbacka",
            "Större entreprenader tas även utanför verksamhetsområdet — kontakta oss för förfrågan",
          ],
          image: IMAGES.solfilmHero,
        },
        {
          eyebrow: "TYPER AV UPPDRAG",
          heading: "Uppdrag i hem, verksamheter och fastighetsbestånd.",
          bullets: [
            "Enskild installation i villa, lägenhet eller uterum",
            "Kontor och butiker — från 50 m² till 5 000 m² glasyta",
            "Ramavtal med fastighetsbolag och kommuner",
            "Akuta säkerhetsärenden efter inbrott eller hot",
            "Skuren dekor- och logotypfolie för glasväggar och skyltfönster",
            "Nyproduktionsprojekt tillsammans med arkitekt och byggherre",
            "Service- och underhållsavtal med årlig inspektion",
          ],
          image: IMAGES.solfilm,
        },
        {
          eyebrow: "TEKNISKA UNDERLAG",
          heading: "Mätningar, simuleringar och tekniska detaljer på plats.",
          paragraphs: [
            "I större projekt behöver du ofta mer underlag än ett produktblad. Vi mäter och visar effekten på dina glas. Vid säkerhetsprojekt tar vi även fram underlag om hur konstruktionen beter sig vid tryckpåverkan.",
          ],
          bullets: [
            "Kostnadsfri temperaturmätning vid fönster med och utan film — visar skillnad i värmeinfall på plats",
            "Energibesparingsberäkning per glastyp — jämförelse av total solenergi med och utan solfilm",
            "Simulering av tryckpåverkan vid explosioner och riskbedömning för större säkerhetsprojekt",
            "Kantförsegling med säkerhetsfog i karm — förstärkning för säkerhetsfilm vid explosionsrisk",
            "Fysiska provbitar och testmontering på ett av era fönster inför beslut",
          ],
          image: IMAGES.solfilm,
        },
      ]}

      related={[
        { to: "/offert", label: "Begär offert", desc: "Berätta vilka fönster du behöver hjälp med." },
        { to: "/om-oss", label: "Om oss", desc: "Lär känna BBN Nordic." },
        { to: "/kontakt", label: "Kontakt", desc: "Ring eller mejla oss direkt." },
      ]}
    >
      <FaqSection
        heading="Frågor inför ett platsbesök."
        items={FAQS}
      />
    </ContentPage>
  ),
});
